// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Assume the reader knows nothing about workers' compensation. Every term of
// art in the interface has an entry here and an ℹ affordance next to it.

export interface Term { term: string; short: string; body: string }

export const GLOSSARY: Record<string, Term> = {
  'serious claim': {
    term: 'Serious claim',
    short: 'A workers’ compensation claim with a week or more off work.',
    body: 'An accepted workers’ compensation claim that resulted in a total absence from work of one working week or more. It is the standard unit in Australian injury statistics because it filters out the very large number of minor claims, whose reporting varies a lot between schemes. Everything on this site labelled "claims" means serious claims unless it says otherwise.',
  },
  'frequency rate': {
    term: 'Frequency rate',
    short: 'Serious claims per million hours worked.',
    body: 'Serious claims per million hours actually worked. This is the fairest way to compare industries, because it adjusts for both the number of people employed and how many hours each of them works — part-time-heavy industries are not flattered, and long-hours industries are not penalised. A frequency rate of 10 means one serious claim for roughly every 100,000 hours worked.',
  },
  'incidence rate': {
    term: 'Incidence rate',
    short: 'Serious claims per 1,000 employees.',
    body: 'Serious claims per 1,000 employees. Simpler than the frequency rate and easier to picture, but it does not adjust for hours worked, so an industry with a lot of part-time staff will look safer on this measure than it is per hour.',
  },
  'median compensation': {
    term: 'Median compensation paid',
    short: 'The middle claim’s total payment, not the average.',
    body: 'The total amount paid on the middle claim, when every finalised claim in that group is lined up from cheapest to most expensive. The median is used rather than the average because a small number of catastrophic claims would otherwise dominate. It covers income replacement plus medical and related costs. Because payments accrue over years, the most recent year is always withheld.',
  },
  'median time lost': {
    term: 'Median time lost',
    short: 'The middle claim’s working weeks off the job.',
    body: 'The number of working weeks of compensated absence on the middle claim in a group. Like median compensation, it is withheld for the most recent year because many of those claims are still open — the worker has not come back yet, so the final figure is not known.',
  },
  np: {
    term: 'NP — not publishable',
    short: 'Withheld, and not the same as zero.',
    body: 'Safe Work Australia suppresses a cell when the annual claim count is under five (to protect identifiable individuals) or when the statistical error on the estimate is too large. It appears on this site as a dash. It is never plotted as zero and never interpolated — a suppressed cell means "some, but fewer than five", not "none".',
  },
  preliminary: {
    term: 'Preliminary',
    short: 'The newest year will be revised upward.',
    body: 'The most recent year of claims data is marked preliminary because claims keep being accepted and finalised after the year ends. It always rises in later releases. For the same reason, the whole of the most recent year is withheld on the two cost measures, and no "record high" claim on this site is ever based on a preliminary year.',
  },
  anzsic: {
    term: 'ANZSIC',
    short: 'The standard industry classification.',
    body: 'The Australian and New Zealand Standard Industrial Classification. Nineteen top-level divisions (A Agriculture through S Other services), each split into subdivisions. This site shows both levels; the divisions are the coloured groupings used consistently across every view.',
  },
  anzsco: {
    term: 'ANZSCO',
    short: 'The standard occupation classification.',
    body: 'The Australian and New Zealand Standard Classification of Occupations. Claims data is published at the two-digit "sub-major group" level (for example 33 Construction Trades Workers). The hazard-exposure data is published at the finer four-digit level, which is why joining the two requires a rollup.',
  },
  toocs: {
    term: 'TOOCS',
    short: 'The injury coding system: what happened, and to what.',
    body: 'The Type of Occurrence Classification System, third edition. It codes each claim four separate ways: the nature of the injury (what is wrong with the person), the bodily location (where), the mechanism of incident (the action or exposure that caused it), and the agency and breakdown agency (the object or substance involved). Safe Work Australia publishes these as four separate tables, not as one linked record — which is why this site does not draw a chain from one to the next.',
  },
  mechanism: {
    term: 'Mechanism of incident',
    short: 'The action or event that caused the injury.',
    body: 'The overall action, exposure or event that best describes how the injury happened — a fall, being hit by a moving object, body stressing (lifting, pushing, repetitive movement), or mental stress. It answers "what happened", not "what got damaged".',
  },
  'body stressing': {
    term: 'Body stressing',
    short: 'Muscular stress from lifting, handling or repetition.',
    body: 'Injury from muscular stress while lifting, carrying, putting down, handling or repeatedly moving — with or without an object. It is consistently the largest single mechanism of serious claims in Australia, well ahead of falls.',
  },
  'mental stress': {
    term: 'Mental stress',
    short: 'The mechanism behind most psychological claims.',
    body: 'The mechanism code covering work pressure, harassment and bullying, exposure to workplace or occupational violence, exposure to a traumatic event, and other mental-stress factors. It is the mechanism that most often produces a mental health condition claim.',
  },
  'mental health condition': {
    term: 'Mental health condition',
    short: 'The nature-of-injury group for psychological injury.',
    body: 'The nature-of-injury group covering psychological and psychiatric conditions arising from work. It is the fastest-growing category of serious claim in Australia and by a wide margin the most expensive per claim in both money and time off work.',
  },
  'traumatic injury fatality': {
    term: 'Traumatic injury fatality',
    short: 'A worker killed by an injury, not by disease.',
    body: 'A person who died from an injury sustained in the course of a work activity. It deliberately excludes deaths from occupational disease — cancers, asbestos-related disease and similar — because those take decades to appear and cannot be attributed to a year of exposure. Those deaths are counted separately and are substantially undercounted everywhere.',
  },
  jurisdiction: {
    term: 'Jurisdiction',
    short: 'A workers’ compensation scheme, which is not the same as a state.',
    body: 'Australia has no single workers’ compensation scheme. Each state and territory runs its own, and there are three more on top: Comcare (Australian Government employees, who work in every state), Seacare (maritime), and in some tables the ACT is split into government and private sectors. A jurisdiction is a scheme, not a place — which is why the figures for schemes must never be added to the figures for states.',
  },
  'field active inspector': {
    term: 'Field active inspector',
    short: 'A regulator’s inspector who actually visits workplaces.',
    body: 'An inspector employed by a work health and safety regulator who spends their time in the field rather than in an office. Expressed per 10,000 employees so jurisdictions of very different sizes can be compared.',
  },
  'proactive visit': {
    term: 'Proactive visit',
    short: 'An inspection nobody asked for.',
    body: 'A workplace visit that did not result from a complaint or an incident — the regulator chose to turn up. The ratio of proactive to reactive visits is a reasonable proxy for whether a regulator is preventing harm or responding to it after the fact.',
  },
  'improvement notice': {
    term: 'Improvement notice',
    short: 'Fix this by a stated date.',
    body: 'A written direction from an inspector requiring a duty holder to remedy a contravention within a set period. The lightest of the formal enforcement tools, and by far the most common.',
  },
  'prohibition notice': {
    term: 'Prohibition notice',
    short: 'Stop this activity now.',
    body: 'A direction to stop an activity immediately because it involves an immediate or imminent risk to someone’s health or safety. It is the most serious thing an inspector can do on the spot, and unlike an improvement notice it has no compliance period — the work stops.',
  },
  'enforceable undertaking': {
    term: 'Enforceable undertaking',
    short: 'A binding promise accepted instead of prosecution.',
    body: 'A legally binding commitment offered by a duty holder and accepted by the regulator as an alternative to prosecution, usually involving substantial spending on safety improvements. Controversial, because it resolves a serious breach without a conviction.',
  },
  premium: {
    term: 'Standardised average premium rate',
    short: 'What employers pay for cover, as a share of payroll.',
    body: 'The average workers’ compensation premium as a percentage of payroll, standardised to a common industry mix so schemes with different economies can be compared. Without that standardisation, a jurisdiction dominated by office work would always look cheap.',
  },
  'hazard exposure score': {
    term: 'Hazard exposure score',
    short: 'How much a job involves a given hazard, 0 to 100.',
    body: 'A 0–100 index of how often or how intensely a typical worker in an occupation is exposed to a particular hazard — climbing ladders, cramped spaces, contaminants, dealing with physically aggressive people. It comes from Safe Work Australia’s Beta Occupational Hazards Dataset, which is derived from the American O*NET survey and mapped onto Australian occupation codes. It describes the job, not the injuries: a high score is exposure, not outcome.',
  },
  'ANZSIC division': {
    term: 'ANZSIC division',
    short: 'The nineteen top-level industry groups.',
    body: 'The nineteen broadest industry groupings, lettered A to S. Each has its own colour on this site, used identically in every chart.',
  },
  nfd: {
    term: 'nfd — not further defined',
    short: 'Coded to a group but no deeper.',
    body: '"Not further defined". A claim coded to a broad group without enough detail to place it in a specific one. These rows are almost always suppressed because the counts are tiny, and they are kept out of every ranking on this site.',
  },
};

export function lookup(term: string): Term | null {
  const key = term.toLowerCase();
  return GLOSSARY[key] ?? GLOSSARY[term] ?? null;
}

/** Render an inline glossary affordance. `key` must exist in GLOSSARY. */
export function gloss(label: string, key: string): string {
  return `<button type="button" class="gloss-link" data-term="${key}" aria-label="What is ${label}?">${label}<span aria-hidden="true">ⓘ</span></button>`;
}
